import { Badge, LevelInfo } from '../types/badges';
import { Medal, Trophy, Crown, Swords, Shield, Award, Star, Sparkles, Flame, Target } from 'lucide-react';

// Playtime Badges
const PLAYTIME_BADGES: Badge[] = [
  {
    id: 'noobish',
    name: 'Noobish',
    description: 'Get 1,000 hours of total playtime',
    icon: Medal,
    requirement: 1000,
    experiencePoints: 1000,
    category: 'playtime'
  },
  {
    id: 'amateur',
    name: 'Amateur',
    description: 'Get 2,000 hours of total playtime',
    icon: Trophy,
    requirement: 2000,
    experiencePoints: 2000,
    category: 'playtime'
  },
  {
    id: 'gamer',
    name: 'Gamer',
    description: 'Get 3,000 hours of total playtime',
    icon: Crown,
    requirement: 3000,
    experiencePoints: 3000,
    category: 'playtime'
  },
  {
    id: 'pro_gamer',
    name: 'Pro Gamer',
    description: 'Get 5,000 hours of total playtime',
    icon: Swords,
    requirement: 5000,
    experiencePoints: 5000,
    category: 'playtime'
  }
];

// Achievement Badges
const ACHIEVEMENT_BADGES: Badge[] = [
  {
    id: 'achievement_hunter_i',
    name: 'Achievement Hunter I',
    description: 'Unlock 50 achievements across all games',
    icon: Target,
    requirement: 50,
    experiencePoints: 500,
    category: 'achievements'
  },
  {
    id: 'achievement_hunter_ii',
    name: 'Achievement Hunter II',
    description: 'Unlock 100 achievements across all games',
    icon: Award,
    requirement: 100,
    experiencePoints: 1000,
    category: 'achievements'
  },
  {
    id: 'achievement_master_i',
    name: 'Achievement Master I',
    description: 'Unlock 250 achievements across all games',
    icon: Star,
    requirement: 250,
    experiencePoints: 2500,
    category: 'achievements'
  },
  {
    id: 'achievement_master_ii',
    name: 'Achievement Master II',
    description: 'Unlock 500 achievements across all games',
    icon: Sparkles,
    requirement: 500,
    experiencePoints: 5000,
    category: 'achievements'
  },
  {
    id: 'achievement_legend',
    name: 'Achievement Legend',
    description: 'Unlock 1,000 achievements across all games',
    icon: Flame,
    requirement: 1000,
    experiencePoints: 10000,
    category: 'achievements'
  }
];

export const BADGES = [...PLAYTIME_BADGES, ...ACHIEVEMENT_BADGES];

export const LEVELS: LevelInfo[] = [
  { level: 1, experienceRequired: 0, title: 'Rookie' },
  { level: 2, experienceRequired: 1000, title: 'Novice' },
  { level: 3, experienceRequired: 3000, title: 'Intermediate' },
  { level: 4, experienceRequired: 6000, title: 'Advanced' },
  { level: 5, experienceRequired: 10000, title: 'Expert' },
  { level: 6, experienceRequired: 15000, title: 'Master' },
  { level: 7, experienceRequired: 21000, title: 'Legend' }
];

export function calculateUserLevel(experience: number): LevelInfo {
  let currentLevel = LEVELS[0];
  
  for (const level of LEVELS) {
    if (experience >= level.experienceRequired) {
      currentLevel = level;
    } else {
      break;
    }
  }
  
  return currentLevel;
}

export function calculateProgress(experience: number): number {
  const currentLevel = calculateUserLevel(experience);
  const currentLevelIndex = LEVELS.findIndex(level => level.level === currentLevel.level);
  const nextLevelIndex = currentLevelIndex + 1;
  
  // If at max level, return 100%
  if (nextLevelIndex >= LEVELS.length) {
    return 100;
  }
  
  const nextLevel = LEVELS[nextLevelIndex];
  const currentLevelXP = currentLevel.experienceRequired;
  const nextLevelXP = nextLevel.experienceRequired;
  const xpInCurrentLevel = experience - currentLevelXP;
  const xpRequiredForNextLevel = nextLevelXP - currentLevelXP;
  
  return Math.min(100, Math.round((xpInCurrentLevel / xpRequiredForNextLevel) * 100));
}

export function calculateBadges(totalPlaytime: number, totalAchievements: number = 0): string[] {
  const playtimeBadges = PLAYTIME_BADGES
    .filter(badge => totalPlaytime >= badge.requirement)
    .map(badge => badge.id);

  const achievementBadges = ACHIEVEMENT_BADGES
    .filter(badge => totalAchievements >= badge.requirement)
    .map(badge => badge.id);

  return [...playtimeBadges, ...achievementBadges];
}

export function calculateTotalExperience(badges: string[]): number {
  return badges.reduce((total, badgeId) => {
    const badge = BADGES.find(b => b.id === badgeId);
    return total + (badge?.experiencePoints || 0);
  }, 0);
}

export function getBadgesByCategory(badges: string[]): Record<string, Badge[]> {
  const earnedBadges = badges.map(id => BADGES.find(b => b.id === id)).filter(Boolean) as Badge[];
  
  return earnedBadges.reduce((acc, badge) => {
    const category = badge.category || 'other';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(badge);
    return acc;
  }, {} as Record<string, Badge[]>);
}